import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { Loader2, Mail, Lock, User, Sparkles, ArrowRight, Cloud } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, register, requestMagicLink, verifyMagicLink } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [autoVerifying, setAutoVerifying] = useState(false);
  
  // Ref to prevent multiple verification attempts
  const verificationAttempted = useRef(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerName, setRegisterName] = useState('');

  // Magic Link form
  const [magicEmail, setMagicEmail] = useState('');
  const [magicToken, setMagicToken] = useState('');

  const from = location.state?.from?.pathname || '/portal';

  // Auto-verify magic link from URL parameters - runs only once
  useEffect(() => {
    const magicFromUrl = searchParams.get('magic');
    const emailFromUrl = searchParams.get('email');
    
    // Only attempt once
    if (magicFromUrl && emailFromUrl && !verificationAttempted.current) {
      verificationAttempted.current = true;
      setAutoVerifying(true);
      console.log('Magic link detected in URL, auto-verifying...');
      
      verifyMagicLink(emailFromUrl, magicFromUrl)
        .then(() => {
          toast.success('Erfolgreich eingeloggt!');
          navigate('/portal', { replace: true });
        })
        .catch((error) => {
          toast.error(error.message || 'Magic Link ungültig oder abgelaufen');
          // Show the magic link form with pre-filled values
          setMagicLinkSent(true);
          setMagicLinkEmail(emailFromUrl);
          setMagicToken(magicFromUrl);
          setAutoVerifying(false);
        });
    }
  }, []); // Empty dependency array - run only on mount

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Bitte fülle alle Felder aus.');
      return;
    }

    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      toast.success('Erfolgreich eingeloggt!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerEmail || !registerPassword) {
      toast.error('Bitte fülle alle Pflichtfelder aus.');
      return;
    }

    if (registerPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein.');
      return;
    }

    setLoading(true);
    try {
      await register(registerEmail, registerPassword, registerName);
      toast.success('Konto erfolgreich erstellt!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestMagicLink = async (e) => {
    e.preventDefault();
    if (!magicEmail) {
      toast.error('Bitte gib deine E-Mail-Adresse ein.');
      return;
    }

    setLoading(true);
    try {
      const result = await requestMagicLink(magicEmail);
      setMagicLinkSent(true);
      setMagicLinkEmail(magicEmail);
      toast.success('Magic Link wurde gesendet!');
      
      // For demo purposes, show the debug token
      if (result.debug_token) {
        console.log('DEBUG: Magic Link Token:', result.debug_token);
        setMagicToken(result.debug_token);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMagicLink = async (e) => {
    e.preventDefault();
    if (!magicToken) {
      toast.error('Bitte gib den Magic Link Token ein.');
      return;
    }

    setLoading(true);
    try {
      await verifyMagicLink(magicLinkEmail, magicToken);
      toast.success('Erfolgreich eingeloggt!');
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12 px-4" data-testid="login-page">
      {/* Auto-verifying Magic Link */}
      {autoVerifying && (
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="text-lg font-medium">Magic Link wird verifiziert...</p>
          <p className="text-muted-foreground">Du wirst gleich eingeloggt.</p>
        </div>
      )}
      
      {!autoVerifying && (
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-2xl font-bold font-display">
            <Cloud className="h-8 w-8 text-primary" />
            Wolkenhelden
          </Link>
          <h1 className="mt-6 text-2xl font-semibold">Willkommen zurück</h1>
          <p className="mt-2 text-muted-foreground">
            Melde dich an, um deine Cloud zu verwalten
          </p>
        </div>

        <Card className="glass-card">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="tab-login">Anmelden</TabsTrigger>
                <TabsTrigger value="register" data-testid="tab-register">Registrieren</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login" className="mt-6 space-y-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">E-Mail</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="deinname@firma.de"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10"
                        data-testid="input-login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Passwort</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10"
                        data-testid="input-login-password"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full" 
                    disabled={loading}
                    data-testid="button-login"
                  >
                    {loading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    Anmelden
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">oder</span>
                  </div>
                </div>

                {/* Magic Link Section */}
                {!magicLinkSent ? (
                  <form onSubmit={handleRequestMagicLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="magic-email">Magic Link anfordern</Label>
                      <div className="relative">
                        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="magic-email"
                          type="email"
                          placeholder="deinname@firma.de"
                          value={magicEmail}
                          onChange={(e) => setMagicEmail(e.target.value)}
                          className="pl-10"
                          data-testid="input-magic-email"
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Wir senden dir einen Link zum Anmelden per E-Mail.
                      </p>
                    </div>

                    <Button 
                      type="submit" 
                      variant="outline" 
                      className="w-full"
                      disabled={loading}
                      data-testid="button-request-magic-link"
                    >
                      {loading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="mr-2 h-4 w-4" />
                      )}
                      Magic Link senden
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyMagicLink} className="space-y-4">
                    <div className="rounded-lg bg-primary/10 p-4 text-sm">
                      <p className="font-medium">Magic Link gesendet!</p>
                      <p className="text-muted-foreground mt-1">
                        Prüfe deine E-Mails ({magicLinkEmail}) und klicke auf den Link oder gib den Code hier ein.
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="magic-token">Token aus E-Mail</Label>
                      <Input
                        id="magic-token"
                        type="text"
                        placeholder="Token eingeben..."
                        value={magicToken}
                        onChange={(e) => setMagicToken(e.target.value)}
                        data-testid="input-magic-token"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full"
                      disabled={loading}
                      data-testid="button-verify-magic-link"
                    >
                      {loading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <ArrowRight className="mr-2 h-4 w-4" />
                      )}
                      Anmelden
                    </Button>

                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full"
                      onClick={() => {
                        setMagicLinkSent(false);
                        setMagicToken('');
                      }}
                    >
                      Neuen Link anfordern
                    </Button>
                  </form>
                )}
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="mt-6 space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Name (optional)</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Max Mustermann"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="pl-10"
                        data-testid="input-register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">E-Mail *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="deinname@firma.de"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="input-register-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Passwort *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="Mindestens 8 Zeichen"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="pl-10"
                        minLength={8}
                        required
                        data-testid="input-register-password"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Mindestens 8 Zeichen
                    </p>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full"
                    disabled={loading}
                    data-testid="button-register"
                  >
                    {loading ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <ArrowRight className="mr-2 h-4 w-4" />
                    )}
                    Konto erstellen
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    Mit der Registrierung akzeptierst du unsere{' '}
                    <Link to="/agb" className="underline hover:text-primary">AGB</Link> und{' '}
                    <Link to="/datenschutz" className="underline hover:text-primary">Datenschutzerklärung</Link>.
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Back to home */}
        <div className="text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">
            ← Zurück zur Startseite
          </Link>
        </div>
      </div>
      )}
    </div>
  );
};

export default LoginPage;
